import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { EmptyState } from '../lib/illustrations';
import { IconUserCog, IconPlus, IconShield, IconPen, IconTrash } from '../lib/icons';

const ROLES = [
  { id: 'admin', label: 'Administrador' },
  { id: 'editor', label: 'Editor' },
];

const roleLabel = (role) => (role === 'admin' ? 'Administrador' : 'Editor');

export default function Users({ user, onRefresh }) {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({ username: '', name: '', password: '', role: 'editor' });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'editor', active: true, password: '' });
  const [mine, setMine] = useState({ currentPassword: '', newPassword: '' });
  const mounted = useRef(true);
  const messageTimer = useRef(null);

  const show = (type, text) => {
    setMessage({ type, text });
    if (messageTimer.current) clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => {
      if (mounted.current) setMessage(null);
    }, 6000);
  };

  const loadUsers = useCallback(async () => {
    try {
      const data = await api.get('/users');
      if (mounted.current) setUsers(data);
    } catch (err) {
      if (mounted.current) setUsers([]);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadUsers();
    return () => {
      mounted.current = false;
      if (messageTimer.current) clearTimeout(messageTimer.current);
    };
  }, [loadUsers]);

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      show('ok', 'Alteração salva.');
    } catch (err) {
      show('err', err.message);
    } finally {
      setBusy('');
    }
  };

  const createUser = () =>
    run('create', async () => {
      await api.post('/users', form);
      setForm({ username: '', name: '', password: '', role: 'editor' });
      await loadUsers();
      onRefresh();
    });

  const startEdit = (item) => {
    setEditing(item.id);
    setEditForm({ name: item.name || '', role: item.role, active: item.active !== false, password: '' });
  };

  const saveEdit = (id) =>
    run(`save-${id}`, async () => {
      await api.put(`/users/${id}`, {
        name: editForm.name,
        role: editForm.role,
        active: editForm.active,
        ...(editForm.password ? { password: editForm.password } : {}),
      });
      setEditing(null);
      await loadUsers();
      onRefresh();
    });

  const remove = (item) => {
    if (!window.confirm(`Excluir o usuário "${item.username}"? Essa ação não pode ser desfeita.`)) return;
    return run(`del-${item.id}`, async () => {
      await api.del(`/users/${item.id}`);
      await loadUsers();
      onRefresh();
    });
  };

  const changeMyPassword = () =>
    run('mine', async () => {
      await api.put('/auth/password', mine);
      setMine({ currentPassword: '', newPassword: '' });
    });

  const adminCount = users.filter((u) => u.role === 'admin' && u.active !== false).length;

  return (
    <div>
      {message ? <div className={`banner ${message.type}`}>{message.text}</div> : null}

      <div className="grid">
        <section className="section">
          <div className="section-head">
            <h2>
              <span className="head-ico">
                <IconPlus size={16} />
              </span>
              Novo usuário
            </h2>
          </div>
          <div className="field">
            <label>Nome de usuário</label>
            <input
              className="input"
              value={form.username}
              autoComplete="off"
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="ex.: maria"
            />
          </div>
          <div className="field">
            <label>Nome completo (opcional)</label>
            <input
              className="input"
              value={form.name}
              autoComplete="off"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="ex.: Maria Silva"
            />
          </div>
          <div className="field">
            <label>Senha</label>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="mínimo de 6 caracteres"
            />
          </div>
          <div className="field">
            <label>Papel</label>
            <select
              className="select"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              {ROLES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="hint" style={{ marginBottom: 10 }}>
            <strong>Administrador</strong>: tudo, inclusive usuários e chaves. <strong>Editor</strong>: publica,
            confirma, gerencia contas, chaves e estatísticas.
          </div>
          <button
            type="button"
            className="btn primary"
            disabled={busy === 'create' || !form.username.trim() || form.password.length < 6}
            onClick={createUser}
          >
            <span className="btn-ico">
              <IconPlus size={15} />
            </span>
            {busy === 'create' ? 'Criando…' : 'Criar usuário'}
          </button>
        </section>

        <section className="section">
          <div className="section-head">
            <h2>
              <span className="head-ico">
                <IconShield size={16} />
              </span>
              Minha senha
            </h2>
          </div>
          <div className="field">
            <label>Senha atual</label>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={mine.currentPassword}
              onChange={(e) => setMine({ ...mine, currentPassword: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Nova senha</label>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={mine.newPassword}
              onChange={(e) => setMine({ ...mine, newPassword: e.target.value })}
              placeholder="mínimo de 6 caracteres"
            />
          </div>
          <button
            type="button"
            className="btn success"
            disabled={busy === 'mine' || !mine.currentPassword || mine.newPassword.length < 6}
            onClick={changeMyPassword}
          >
            {busy === 'mine' ? 'Salvando…' : 'Trocar senha'}
          </button>
        </section>
      </div>

      <section className="section" style={{ marginTop: 18 }}>
        <div className="section-head">
          <h2>
            <span className="head-ico">
              <IconUserCog size={16} />
            </span>
            Usuários ({users.length})
          </h2>
          <span className="hint">{adminCount} administrador(es) ativo(s)</span>
        </div>

        <div className="list">
          {users.map((item) => {
            const isMe = user && user.id === item.id;
            const active = item.active !== false;
            const lastAdmin = item.role === 'admin' && active && adminCount <= 1;
            return (
              <div className="row" key={item.id} style={{ flexWrap: 'wrap' }}>
                <div style={{ minWidth: 200, flex: 1 }}>
                  <div className="title">
                    {item.name || item.username}{' '}
                    <span className={`badge ${item.role === 'admin' ? 'ok' : 'info'}`} style={{ marginLeft: 6 }}>
                      {roleLabel(item.role)}
                    </span>
                    {!active ? (
                      <span className="badge err" style={{ marginLeft: 6 }}>
                        inativo
                      </span>
                    ) : null}
                    {isMe ? <span className="badge muted" style={{ marginLeft: 6 }}>você</span> : null}
                  </div>
                  <div className="meta">@{item.username}</div>
                  <div className="meta">
                    {item.lastLoginAt ? `último acesso ${new Date(item.lastLoginAt).toLocaleString('pt-BR')}` : 'nunca entrou'}
                  </div>
                </div>

                {editing === item.id ? (
                  <div style={{ minWidth: 260, flex: 1 }}>
                    <div className="field">
                      <label>Nome</label>
                      <input
                        className="input"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      />
                    </div>
                    <div className="field">
                      <label>Senha (deixe em branco para manter)</label>
                      <input
                        className="input"
                        type="password"
                        autoComplete="new-password"
                        value={editForm.password}
                        onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                      />
                    </div>
                    <div className="field" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <select
                        className="select"
                        value={editForm.role}
                        disabled={lastAdmin && editForm.role !== 'admin'}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                      >
                        {ROLES.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                      <label className="switch-row">
                        <input
                          type="checkbox"
                          checked={editForm.active}
                          disabled={lastAdmin && editForm.active}
                          onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                        />
                        Ativo
                      </label>
                    </div>
                    <div className="actions">
                      <button
                        type="button"
                        className="btn success small"
                        disabled={busy === `save-${item.id}`}
                        onClick={() => saveEdit(item.id)}
                      >
                        {busy === `save-${item.id}` ? '…' : 'Salvar'}
                      </button>
                      <button type="button" className="btn ghost small" onClick={() => setEditing(null)}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                    <div className="actions">
                      <button type="button" className="btn ghost small" onClick={() => startEdit(item)}>
                        <span className="btn-ico">
                          <IconPen size={14} />
                        </span>
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn danger small"
                        disabled={busy === `del-${item.id}` || isMe || lastAdmin}
                        onClick={() => remove(item)}
                      >
                        <span className="btn-ico">
                          <IconTrash size={14} />
                        </span>
                        {busy === `del-${item.id}` ? '…' : 'Excluir'}
                      </button>
                    </div>
                )}
              </div>
            );
          })}
          {users.length === 0 ? (
            <EmptyState kind="users" size={160} title="Nenhum usuário carregado." />
          ) : null}
        </div>

        <div className="hint" style={{ marginTop: 12 }}>
          O último administrador não pode ser excluído nem desativado. Usuários desativados são desconectados na hora.
        </div>
      </section>
    </div>
  );
}
